import {FC, useId, useState} from "react";
import {
    Button2,
    Composite,
    DatePicker,
    Drop,
    IconBox,
    Input,
    RadioGroup,
    Tooltip,
    Type as T,
} from "@infobis/ui-styles-utils";
import colors from "@infobis/ui-styles-utils/styles/_colors";
import {ApplicationDataState} from "../../types";
import st from './formBody.module.scss';
import {maxStrValidator, minNumValidator} from "@pages/partners/components/applicationForm/utils";
import {format} from "date-fns";

interface FormBodyProps {
    item: ApplicationDataState
    actions: any
    options: {
        analysisObjects: { id: string, label: string }[]
        testTypes: { id: string, label: string }[]
        needSelection: { id: string, label: string }[]
    }
    container: HTMLDivElement
};

const PHONE_VALIDATOR = Input.Validator(...minNumValidator(11));
const STR_VALIDATOR = Input.Validator(...maxStrValidator(255));
const FormBody: FC<FormBodyProps> = ({item, actions, options, container}) => {

    const {analysisObjects, testTypes, needSelection} = options;

    const uniqueId = useId();
    const [errString, setErrString] = useState('');
    Input.useForm(uniqueId, errString, Boolean(errString), () => {
        const errString = item.expectedDeadline ? '' : 'Обязательный выбор даты';
        setErrString(errString);
        return !Boolean(errString);
    });

    return (
        <>
            <Input.Text
                style={{width: '552px'}}
                validators={Input.useValidator(STR_VALIDATOR.fn!, STR_VALIDATOR.message)}
                label={'Название организации'}
                width={T.Width.FULL}
                value={item.companyName}
                onChange={actions.setCompanyName}
                required
            />
            <Input.Text
                validators={Input.useValidator(STR_VALIDATOR.fn!, STR_VALIDATOR.message)}
                label={'Адрес'}
                width={T.Width.FULL}
                value={item.address}
                onChange={actions.setAddress}
                required
            />
            <div className={st['multiple-inputs']}>
                <Input.Mask
                    validators={Input.useValidator(PHONE_VALIDATOR.fn!, PHONE_VALIDATOR.message)}
                    maskProps={{mask: "+7 999 999 99 99", maskPlaceholder: null}}
                    className={st['multiple-inputs__item']}
                    label={'Телефон'}
                    width={T.Width.FULL}
                    value={item.phone}
                    onChange={actions.setPhone}
                    placeholder={'Введите номер телефона'}
                    required
                />
                <Input.Text
                    validators={Input.useValidator(STR_VALIDATOR.fn!, STR_VALIDATOR.message)}
                    className={st['multiple-inputs__item']}
                    label={'E-Mail'}
                    width={T.Width.FULL}
                    value={item.email}
                    onChange={actions.setEmail}
                    validateType={'email'}
                    required
                />
            </div>
            <div className={st['multiple-inputs']}>
                <Drop.Select
                    className={st['multiple-inputs__item']}
                    label={'Объект анализа'}
                    width={T.Width.FULL}
                    options={analysisObjects}
                    value={item.testObject || undefined}
                    customOptions={(c) => ({
                        label: c.label,
                        id: c.id,
                    })}
                    uniq={'id'}
                    onChange={actions.setTestObject}
                    pickerFieldProps={{portalProps: {container}}}
                    search={false}
                    required
                />
                <Input.NumberFloat
                    maskProps={undefined}
                    className={st['multiple-inputs__item']}
                    label={'Площадь обследования'}
                    width={T.Width.FULL}
                    value={item.testArea}
                    suffix={'га'}
                    decimals={3}
                    numberType={'float'}
                    style={{maxWidth: '165px'}}
                    onChange={actions.setTestArea}
                    required
                />
            </div>
            <Composite>
                <Input.NumberFloat
                    maskProps={undefined}
                    label={'Количество полей'}
                    width={T.Width.FULL}
                    value={item.fieldCount!}
                    onChange={(v) => actions.setFieldCount(Number(v))}
                    decimals={0}
                    numberType={'integer'}
                    required
                />
                <Input.NumberFloat
                    maskProps={undefined}
                    label={'Количество образцов'}
                    width={T.Width.FULL}
                    value={item.samplesCount!}
                    onChange={(v) => actions.setSamplesCount(Number(v))}
                    decimals={0}
                    numberType={'integer'}
                    required
                />
            </Composite>
            <RadioGroup
                title={'Необходима услуга по отбору образцов?'}
                size={T.Size.LARGE}
                checked={item.needSelection}
                variants={needSelection}
                onChange={actions.setNeedSelection}
            />
            <DatePicker
                dateFormat={'dd.MM.yyyy, HH:mm'}
                value={item.expectedDeadline || undefined}
                onChange={(d) => {
                    actions.setExpectedDeadline(d as [Date])
                    const errString = d ? '' : 'Обязательный выбор даты';
                    setErrString(errString);
                }}
                portalProps={{container}}
                withInput
            >
                {(props) => {

                    return (
                        <div style={{display: 'flex', alignItems: 'center', gap: '16px'}}>
                            <label>{'Желаемый срок предоставления результатов'}</label>
                            <div onClick={props.togglePicker} ref={props.ref}>
                                <Button2.Clear.IconL icon={T.IconName.Calendar} size={T.Size.SMALL}>
                                    {item.expectedDeadline?.map(i => format(i, 'dd.MM.yyyy')) || 'Выберите дату'}
                                </Button2.Clear.IconL>
                            </div>
                            {errString && (
                                <div style={{marginLeft: 'auto'}}><Tooltip style={{zIndex: 1000}} text={errString}>
                                    <IconBox
                                        icon={T.IconName.Info}
                                        color={colors.TYPO_ALERT}
                                        size={T.Size.LARGE}
                                    />
                                </Tooltip></div>
                            )}
                        </div>
                    )
                }}
            </DatePicker>
            <RadioGroup
                title={'Виды анализов для почв'}
                size={T.Size.LARGE}
                checked={item.testType}
                variants={testTypes}
                onChange={actions.setTestType}
            />
            <Input.Text
                validators={Input.useValidator(STR_VALIDATOR.fn!, STR_VALIDATOR.message)}
                width={T.Width.FULL}
                value={item.freeSoilTestList}
                onChange={actions.setFreeSoilTestList}
                placeholder={'Укажите перечень вручную'}
                // required={item.testType === 'config'}
                disabled={item.testType !== 'config'}
            />
        </>
    )
};

export default FormBody